/**
 * property-branding.js — manager-workspace white-label (Issue #691).
 *
 * Property branding (logo, show_name, appearance) lives on the property.
 * Per-browser overrides live in localStorage under
 * `luboo-property-view:{propertyId}`.
 */

import { lubooRequest } from '../mixins/data-channel.mixin.js';

const VIEW_DEFAULTS = {
  useLogo: true,
  useAppearance: true,
  showName: true,
};

function viewKey(propertyId) {
  return `luboo-property-view:${propertyId}`;
}

function readView(propertyId) {
  const branding = window.LUBOO_CURRENT_PROPERTY?.branding || {};
  const defaults = {
    ...VIEW_DEFAULTS,
    showName: branding.show_name !== false,
  };
  try {
    const raw = localStorage.getItem(viewKey(propertyId));
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch (_) {
    return defaults;
  }
}

function writeView(propertyId, view) {
  try {
    localStorage.setItem(viewKey(propertyId), JSON.stringify(view));
  } catch (_) {
    /* quota / disabled */
  }
}

function currentProperty() {
  return window.LUBOO_CURRENT_PROPERTY || null;
}

function applyAppearance(prop, view) {
  if (!window.themePrefs?.setOverlay) return;
  if (view.useAppearance) {
    window.themePrefs.setOverlay(prop.branding || {});
  } else {
    window.themePrefs.setOverlay(null);
  }
}

function applySidebar(prop, view) {
  const brand = document.querySelector('.sidebar-brand');
  if (!brand) return;
  const logoWrap = brand.querySelector('.sidebar-brand-logo');
  const img = brand.querySelector('[data-role="property-logo"]');
  const text = brand.querySelector('.sidebar-brand-text');
  const branding = prop.branding || {};
  const logoUrl = branding.logo_url;
  const useLogo = Boolean(view.useLogo && logoUrl);

  if (logoWrap) {
    if (useLogo) logoWrap.setAttribute('data-custom-logo', '');
    else logoWrap.removeAttribute('data-custom-logo');
  }
  if (img) {
    if (useLogo) {
      img.src = logoUrl;
      img.hidden = false;
      img.alt = prop.name || 'Property logo';
    } else {
      img.removeAttribute('src');
      img.hidden = true;
      img.alt = '';
    }
  }

  const showName = view.showName !== false;
  if (text) {
    if (useLogo) {
      text.textContent = prop.name || '';
      text.hidden = !showName;
    } else {
      text.textContent = 'Lunaria';
      text.hidden = false;
    }
  }
  if (useLogo && !showName) brand.setAttribute('data-hide-name', '');
  else brand.removeAttribute('data-hide-name');
}

function applyChrome() {
  const prop = currentProperty();
  if (!prop?.id) {
    window.themePrefs?.setOverlay?.(null);
    return;
  }
  const view = readView(prop.id);
  applyAppearance(prop, view);
  applySidebar(prop, view);
  syncSettingsUi(prop, view);
}

function syncSettingsUi(prop, view) {
  document.querySelectorAll('[data-property-branding]').forEach((root) => {
    syncOneSettingsUi(root, prop, view);
  });
}

function syncOneSettingsUi(root, prop, view) {
  const branding = prop.branding || {};
  const preview = root.querySelector('[data-role="branding-logo-preview"]');
  if (preview) {
    if (branding.logo_url) {
      preview.src = branding.logo_url;
      preview.hidden = false;
    } else {
      preview.removeAttribute('src');
      preview.hidden = true;
    }
  }
  const deleteBtn = root.querySelector('[data-action="property:delete-logo"]');
  if (deleteBtn) deleteBtn.hidden = !branding.logo_url;

  const showName = root.querySelector('[data-branding="show_name"]');
  if (showName) showName.checked = branding.show_name !== false;

  root.querySelectorAll('[data-view]').forEach((input) => {
    const key = input.dataset.view;
    if (key in view) input.checked = Boolean(view[key]);
  });
}

async function saveShowName(checked) {
  const prop = currentProperty();
  if (!prop?.id) return;
  const branding = {
    theme: prop.branding?.theme ?? null,
    accent: prop.branding?.accent ?? null,
    density: prop.branding?.density ?? null,
    radius: prop.branding?.radius ?? null,
    show_name: checked,
    light: prop.branding?.light ?? null,
    dark: prop.branding?.dark ?? null,
  };
  const updated = await lubooRequest(
    `luboo:property:${prop.id}`,
    'update',
    { propertyId: prop.id, body: { branding } },
    { group: 'property', timeout: 15000 },
  );
  if (updated?.branding) prop.branding = updated.branding;
  else prop.branding = { ...(prop.branding || {}), show_name: checked };
  applyChrome();
}

async function uploadLogo(file) {
  const prop = currentProperty();
  if (!prop?.id || !file) return;
  const result = await lubooRequest(
    `luboo:property:${prop.id}`,
    'upload_logo',
    { propertyId: prop.id, file },
    { group: 'property', timeout: 30000 },
  );
  if (result?.branding) prop.branding = result.branding;
  else if (result?.logo_url) {
    prop.branding = { ...(prop.branding || {}), logo_url: result.logo_url };
  }
  applyChrome();
}

async function deleteLogo() {
  const prop = currentProperty();
  if (!prop?.id) return;
  const result = await lubooRequest(
    `luboo:property:${prop.id}`,
    'delete_logo',
    { propertyId: prop.id },
    { group: 'property', timeout: 15000 },
  );
  if (result?.branding) prop.branding = result.branding;
  else if (prop.branding) prop.branding.logo_url = null;
  applyChrome();
}

function onClick(e) {
  const root = e.target.closest('[data-property-branding]');
  if (!root) return;

  const uploadBtn = e.target.closest('[data-action="property:upload-logo"]');
  if (uploadBtn) {
    root.querySelector('[data-role="branding-logo-input"]')?.click();
    return;
  }
  const deleteBtn = e.target.closest('[data-action="property:delete-logo"]');
  if (deleteBtn) {
    deleteLogo().catch((err) => console.warn('[property-branding] delete failed', err));
  }
}

function onChange(e) {
  const root = e.target.closest('[data-property-branding]');
  if (!root) return;
  const prop = currentProperty();
  if (!prop?.id) return;

  if (e.target.matches('[data-role="branding-logo-input"]')) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) {
      uploadLogo(file).catch((err) => console.warn('[property-branding] upload failed', err));
    }
    return;
  }

  if (e.target.matches('[data-branding="show_name"]')) {
    saveShowName(e.target.checked).catch((err) => {
      console.warn('[property-branding] show_name save failed', err);
    });
    return;
  }

  const viewInput = e.target.closest('[data-view]');
  if (viewInput) {
    const view = readView(prop.id);
    view[viewInput.dataset.view] = viewInput.checked;
    writeView(prop.id, view);
    applyChrome();
  }
}

function listenPropertyChannel(propertyId) {
  try {
    const channel = new BroadcastChannel(`luboo:property:${propertyId}`);
    channel.onmessage = (event) => {
      if (event.data?.type !== 'property_updated') return;
      const payload = event.data.data || {};
      const prop = currentProperty();
      if (!prop) return;
      if (payload.branding) prop.branding = payload.branding;
      if (payload.name) prop.name = payload.name;
      applyChrome();
    };
  } catch (_) {
    /* BroadcastChannel unavailable */
  }
}

function init() {
  if (typeof document === 'undefined') return;
  if (window.__lubooPropertyBrandingBound) {
    applyChrome();
    return;
  }
  window.__lubooPropertyBrandingBound = true;

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  window.addEventListener('luboo:property-branding-changed', applyChrome);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.('[data-property-branding]')
          || node.querySelector?.('[data-property-branding]')
        ) {
          applyChrome();
          return;
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  const prop = currentProperty();
  if (prop?.id) listenPropertyChannel(prop.id);
  applyChrome();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.__lubooApplyPropertyBranding = applyChrome;
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
}
